/**
 * `/leads/:id` — Lead detail with contact info, status management, conversation thread,
 * contact edit form (L4), and booking link section (L4).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAddMessage,
  useLeadDetail,
  useLeadMessages,
  useLeadSequences,
  useStartSequence,
  useUpdateContact,
  useUpdateLeadStatus,
  useUpdateSequenceStatus,
} from "@/hooks/use-leads";
import { useEnrollQuoteFollowup, useQuoteSequences, useRequestQuoteDraft } from "@/hooks/use-quote";
import { useBookingLink } from "@/hooks/use-settings";
import type { LeadMessageResponse, LeadResponse, StartSequenceRequest } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

const STATUS_TRANSITIONS: Record<LeadResponse["status"], Array<LeadResponse["status"]>> = {
  new: ["contacted", "lost"],
  contacted: ["qualified", "lost"],
  qualified: ["booked", "lost"],
  booked: ["closed", "lost"],
  closed: [],
  lost: [],
};

const STATUS_COLORS: Record<LeadResponse["status"], string> = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  qualified: "bg-purple-100 text-purple-800",
  booked: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
  lost: "bg-red-100 text-red-700",
};

// ── Delivery status badge ─────────────────────────────────────────────────────

function DeliveryStatus({ metadata }: { metadata: Record<string, unknown> }) {
  const status = typeof metadata.delivery_status === "string" ? metadata.delivery_status : null;
  if (!status) return null;

  const config: Record<string, { label: string; className: string }> = {
    delivered: { label: "Delivered", className: "text-green-600" },
    sent: { label: "Sent", className: "text-gray-400" },
    queued: { label: "Queued", className: "text-gray-400" },
    failed: { label: "Failed", className: "text-red-500" },
    undelivered: { label: "Undelivered", className: "text-red-500" },
  };
  const cfg = config[status];
  if (!cfg) return null;

  return <span className={`text-[10px] ${cfg.className}`}>{cfg.label}</span>;
}

// ── Message bubble ────────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: LeadMessageResponse }) {
  const isOutbound = message.direction === "outbound";
  return (
    <div className={`flex ${isOutbound ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-sm rounded-2xl px-4 py-2.5 text-sm ${
          isOutbound
            ? "bg-primary text-primary-foreground"
            : message.channel === "internal_note"
              ? "bg-yellow-50 border border-yellow-200 text-yellow-900"
              : "bg-gray-100 text-gray-900"
        }`}
      >
        <p className="whitespace-pre-wrap">{message.body}</p>
        <div
          className={`mt-1 flex items-center gap-1.5 text-[10px] ${isOutbound ? "text-primary-foreground/70 justify-end" : "text-muted-foreground"}`}
        >
          <span>
            {message.channel} · {formatTimestamp(message.sent_at)}
          </span>
          {isOutbound && message.channel === "sms" && (
            <DeliveryStatus metadata={message.metadata} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Contact edit form (L4) ────────────────────────────────────────────────────

interface ContactEditFormProps {
  contactId: string;
  leadId: string;
  initial: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    company: string | null;
  };
  onDone: () => void;
}

function ContactEditForm({ contactId, leadId, initial, onDone }: ContactEditFormProps) {
  const updateContact = useUpdateContact(leadId);
  const [firstName, setFirstName] = useState(initial.first_name ?? "");
  const [lastName, setLastName] = useState(initial.last_name ?? "");
  const [email, setEmail] = useState(initial.email ?? "");
  const [phone, setPhone] = useState(initial.phone ?? "");
  const [company, setCompany] = useState(initial.company ?? "");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateContact.mutate(
      {
        contactId,
        body: {
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          company: company.trim() || null,
        },
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-3 border-t border-gray-100">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-first" className="text-xs">
            First name
          </Label>
          <Input
            id="edit-first"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-last" className="text-xs">
            Last name
          </Label>
          <Input
            id="edit-last"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="edit-email" className="text-xs">
          Email
        </Label>
        <Input
          id="edit-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="edit-phone" className="text-xs">
          Phone (E.164)
        </Label>
        <Input
          id="edit-phone"
          placeholder="+15551234567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="edit-company" className="text-xs">
          Company
        </Label>
        <Input
          id="edit-company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="h-8 text-sm"
        />
      </div>
      {updateContact.isError && (
        <p className="text-xs text-destructive">
          {updateContact.error instanceof Error ? updateContact.error.message : "Failed to save"}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={updateContact.isPending}>
          {updateContact.isPending ? "Saving…" : "Save contact"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ── Booking link section (L4) ─────────────────────────────────────────────────

function BookingLinkSection() {
  const bookingLink = useBookingLink();
  const url = bookingLink.data?.booking_url;

  if (bookingLink.isLoading) return null;
  if (!url) return null;

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Book a time</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">Share your scheduling link with this lead.</p>
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-mono text-primary hover:underline truncate max-w-xs"
          >
            {url}
          </a>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => void navigator.clipboard.writeText(url)}
          >
            Copy
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Add sequence dropdown ─────────────────────────────────────────────────────

const SEQUENCE_OPTIONS: Array<{
  value: StartSequenceRequest["template_name"];
  label: string;
  description: string;
}> = [
  {
    value: "initial_response",
    label: "Initial response",
    description: "Greet and qualify a new lead",
  },
  {
    value: "quote_follow_up",
    label: "Quote follow-up",
    description: "Nudge after sending a quote",
  },
  { value: "re_engagement", label: "Re-engagement", description: "Re-activate a cold lead" },
];

interface AddSequenceMenuProps {
  leadId: string;
  isPending: boolean;
  onStart: (template: StartSequenceRequest["template_name"]) => void;
}

function AddSequenceMenu({ leadId: _leadId, isPending, onStart }: AddSequenceMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        disabled={isPending}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        + Add sequence
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-10 w-56 rounded-lg border border-gray-200 bg-white shadow-md py-1"
        >
          {SEQUENCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              role="menuitem"
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
              onClick={() => {
                onStart(opt.value);
                setOpen(false);
              }}
            >
              <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
              <span className="block text-xs text-muted-foreground">{opt.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const lead = useLeadDetail(id);
  const messages = useLeadMessages(id);
  const updateStatus = useUpdateLeadStatus();
  const addMessage = useAddMessage();

  const sequences = useLeadSequences(id);
  const startSequence = useStartSequence();
  const updateSeqStatus = useUpdateSequenceStatus();

  const [replyText, setReplyText] = useState("");
  const [replyChannel, setReplyChannel] = useState<"sms" | "email" | "internal_note">("sms");
  const [editingContact, setEditingContact] = useState(false);

  // Default the reply channel to match the lead's primary contact method.
  const contactPhone = lead.data?.contact.phone;
  const contactEmail = lead.data?.contact.email;
  useEffect(() => {
    if (contactPhone) {
      setReplyChannel("sms");
    } else if (contactEmail) {
      setReplyChannel("email");
    }
  }, [contactPhone, contactEmail]);

  if (!id) return null;

  if (lead.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-60 w-full rounded-xl" />
      </div>
    );
  }

  if (lead.isError || !lead.data) {
    return (
      <div className="flex flex-col items-center gap-4 p-12 text-center">
        <p className="text-sm text-destructive">Lead not found or failed to load.</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/leads")}>
          Back to leads
        </Button>
      </div>
    );
  }

  const { contact, ...leadData } = lead.data;
  // Use the full message thread (L4.3 fix: recent_messages is capped at 20; messages hook is unlimited)
  const allMessages = messages.data?.items ?? lead.data.recent_messages;
  const transitions = STATUS_TRANSITIONS[leadData.status];

  function sendReply() {
    if (!replyText.trim() || !id) return;
    addMessage.mutate(
      { leadId: id, direction: "outbound", channel: replyChannel, body: replyText.trim() },
      { onSuccess: () => setReplyText("") },
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate("/leads")}
            className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1 hover:underline"
          >
            ← Leads
          </button>
          <h1 className="text-xl font-extrabold tracking-tight text-gray-900">
            {contact.first_name || contact.last_name
              ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
              : (contact.phone ?? contact.email ?? "Unknown")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {contact.phone} {contact.phone && contact.email && "·"} {contact.email}
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[leadData.status]}`}
        >
          {leadData.status}
        </span>
      </header>

      {/* Status transitions */}
      {transitions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {transitions.map((nextStatus) => (
            <Button
              key={nextStatus}
              variant="outline"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => updateStatus.mutate({ id, body: { status: nextStatus } })}
            >
              Move to {nextStatus}
            </Button>
          ))}
        </div>
      )}

      {/* Quote actions (M-QUOTE.3 polish) */}
      <QuoteActionsCard leadId={id} />

      {/* Contact info + edit form (L4) */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-gray-700">Contact</CardTitle>
          {!editingContact && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditingContact(true)}
              className="h-7 text-xs"
            >
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            {contact.company && (
              <>
                <span className="text-muted-foreground">Company</span>
                <span>{contact.company}</span>
              </>
            )}
            {contact.source && (
              <>
                <span className="text-muted-foreground">Source</span>
                <span>{contact.source}</span>
              </>
            )}
            {leadData.source && (
              <>
                <span className="text-muted-foreground">Lead source</span>
                <span>{leadData.source}</span>
              </>
            )}
            {leadData.assigned_to && (
              <>
                <span className="text-muted-foreground">Assigned to</span>
                <span>{leadData.assigned_to}</span>
              </>
            )}
            <span className="text-muted-foreground">Created</span>
            <span>{formatTimestamp(leadData.created_at)}</span>
            {leadData.first_contacted_at && (
              <>
                <span className="text-muted-foreground">First contacted</span>
                <span>{formatTimestamp(leadData.first_contacted_at)}</span>
              </>
            )}
          </div>
          {editingContact && (
            <ContactEditForm
              contactId={contact.id}
              leadId={id}
              initial={{
                first_name: contact.first_name,
                last_name: contact.last_name,
                email: contact.email,
                phone: contact.phone,
                company: contact.company,
              }}
              onDone={() => setEditingContact(false)}
            />
          )}
        </CardContent>
      </Card>

      {/* Booking link section (L4) */}
      <BookingLinkSection />

      {/* Conversation */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-700">Conversation</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {allMessages.length === 0 && (
            <p className="text-sm text-muted-foreground">No messages yet.</p>
          )}
          {allMessages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}

          {/* Reply box */}
          <div className="flex flex-col gap-2 border-t pt-3 mt-2">
            <div className="flex gap-2">
              {(["sms", "email", "internal_note"] as const).map((ch) => (
                <button
                  type="button"
                  key={ch}
                  onClick={() => setReplyChannel(ch)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    replyChannel === ch
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-gray-200 text-muted-foreground hover:border-gray-400"
                  }`}
                >
                  {ch === "internal_note" ? "Note" : ch.toUpperCase()}
                </button>
              ))}
            </div>
            <Textarea
              placeholder={
                replyChannel === "internal_note" ? "Add internal note…" : "Write a message…"
              }
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={sendReply}
                disabled={!replyText.trim() || addMessage.isPending}
              >
                {addMessage.isPending ? "Sending…" : "Send"}
              </Button>
              {addMessage.isError && (
                <span className="text-xs text-destructive">
                  {addMessage.error instanceof Error ? addMessage.error.message : "Failed to send"}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Follow-up sequences */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold text-gray-700">
              Follow-up Sequences
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Automate follow-up messages over time.
            </p>
          </div>
          <AddSequenceMenu
            leadId={id}
            isPending={startSequence.isPending}
            onStart={(template) =>
              id &&
              startSequence.mutate({ leadId: id, body: { template_name: template, metadata: {} } })
            }
          />
        </CardHeader>
        <CardContent>
          {sequences.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
          {sequences.data && sequences.data.items.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No active sequences. Use "Add sequence" to start automated follow-up.
            </p>
          )}
          {sequences.data && sequences.data.items.length > 0 && (
            <div className="flex flex-col gap-2">
              {sequences.data.items.map((seq) => (
                <div
                  key={seq.id}
                  className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 text-sm"
                >
                  <div>
                    <span className="font-medium capitalize">
                      {seq.template_name.replace(/_/g, " ")}
                    </span>
                    <span
                      className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full ${
                        seq.status === "active"
                          ? "bg-green-100 text-green-700"
                          : seq.status === "paused"
                            ? "bg-yellow-100 text-yellow-700"
                            : seq.status === "cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {seq.status}
                    </span>
                  </div>
                  {seq.status === "active" && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-destructive"
                      onClick={() =>
                        id &&
                        updateSeqStatus.mutate({
                          leadId: id,
                          sequenceId: seq.id,
                          body: { status: "cancelled" },
                        })
                      }
                    >
                      Cancel
                    </button>
                  )}
                  {seq.status === "paused" && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() =>
                        id &&
                        updateSeqStatus.mutate({
                          leadId: id,
                          sequenceId: seq.id,
                          body: { status: "active" },
                        })
                      }
                    >
                      Resume
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LeadDetailPage;

/**
 * Quote module actions on the lead detail page (M-QUOTE.3 polish).
 *
 * Two operator actions:
 *   - "Generate AI draft" — emits `quote/draft.requested` to Inngest; a
 *     pending_approval row lands in `/quote/drafts` once the orchestrator
 *     finishes the workflow (typically 5-15s).
 *   - Sequence enrollment — pick a quote follow-up sequence and enroll
 *     this lead; the orchestrator drives the steps.
 */
function QuoteActionsCard({ leadId }: { leadId: string }) {
  const sequences = useQuoteSequences();
  const requestDraft = useRequestQuoteDraft();
  const enroll = useEnrollQuoteFollowup();
  const [sequenceId, setSequenceId] = useState<string>("");

  const hasSequences = (sequences.data?.items.length ?? 0) > 0;

  return (
    <Card className="border-gray-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-gray-700">Quote actions</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={requestDraft.isPending}
            onClick={() => requestDraft.mutate({ lead_id: leadId })}
          >
            {requestDraft.isPending ? "Queued…" : "Generate AI draft"}
          </Button>
          {requestDraft.isSuccess && (
            <span className="text-xs text-muted-foreground">
              Queued — check{" "}
              <a href="/quote/drafts" className="underline">
                Drafts
              </a>{" "}
              shortly.
            </span>
          )}
          {requestDraft.error && (
            <span className="text-xs text-destructive">{requestDraft.error.message}</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {hasSequences ? (
            <>
              <select
                className="rounded border border-input bg-background px-3 py-2 text-sm"
                value={sequenceId}
                onChange={(e) => setSequenceId(e.target.value)}
              >
                <option value="">Pick a sequence…</option>
                {sequences.data?.items.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                disabled={!sequenceId || enroll.isPending}
                onClick={() =>
                  enroll.mutate(
                    { lead_id: leadId, sequence_id: sequenceId },
                    { onSuccess: () => setSequenceId("") },
                  )
                }
              >
                {enroll.isPending ? "Enrolling…" : "Enroll in sequence"}
              </Button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground">
              No quote sequences yet — create one at{" "}
              <a href="/quote/sequences" className="underline">
                Quote › Sequences
              </a>
              .
            </span>
          )}
          {enroll.isSuccess && <span className="text-xs text-muted-foreground">Enrolled ✓</span>}
          {enroll.error && <span className="text-xs text-destructive">{enroll.error.message}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
